/**
 * Cosmetics — renders shop cosmetics that a person has equipped.
 *
 * The equipped classes live on `profiles.active_cosmetics` (`frame-gold`,
 * `frame-cosmic`, `name-neon`) and the visual treatment lives in index.css so
 * it can be reused wherever an avatar or a name is rendered.
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { useInventory, type ActiveCosmetics } from '@/hooks/useInventory';

const FRAME_CLASS: Record<string, string> = {
  'frame-gold': 'cosmetic-frame cosmetic-frame-gold',
  'frame-cosmic': 'cosmetic-frame cosmetic-frame-cosmic',
};

const NAME_CLASS: Record<string, string> = {
  'name-neon': 'cosmetic-name-neon',
};

export function frameClassFor(cosmetics: ActiveCosmetics | undefined): string | undefined {
  const key = cosmetics?.frame;
  return key ? FRAME_CLASS[key] : undefined;
}

export function nameClassFor(cosmetics: ActiveCosmetics | undefined): string | undefined {
  const key = cosmetics?.name;
  return key ? NAME_CLASS[key] : undefined;
}

/** Wraps an avatar with the buyer's equipped frame, if any. */
export const CosmeticFrame: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => {
  const { cosmetics } = useInventory();
  const frame = frameClassFor(cosmetics);
  if (!frame) return <>{children}</>;
  return <div className={cn('inline-flex', frame, className)}>{children}</div>;
};

/** Applies the buyer's equipped name glow to their display name. */
export const CosmeticName: React.FC<{ className?: string; children: React.ReactNode }> = ({
  className,
  children,
}) => {
  const { cosmetics } = useInventory();
  return <span className={cn(nameClassFor(cosmetics), className)}>{children}</span>;
};
